import React, { useState, useEffect } from "react";
import "../AddEntity/AddEntity.css";
import ApiManager from "../../ApiManager/ApiManager";
import MapInput from "../MapInput";
import CustomSelect from "../CustomSelect";
import CustomFileUpload from "../CustomFileUpload/CustomFileUpload";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { formatDateTime } from '../../utils/dateUtils';

const EditEntity = ({
  title,
  prevData,
  updateFunction,
  columns,
  toggleFunction,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  console.log("Initial formData:", prevData);

  useEffect(() => {
    console.log("Updated formData inside usestate:", formData);
  }, [formData]);

  useEffect(() => {
    if (!prevData) return;

    const filteredData = columns.reduce((acc, field) => {
      acc[field.name] = prevData?.[field.name] ?? "";
      return acc;
    }, {});

    setFormData(filteredData);
  }, [prevData, columns]);

  const fetchFile = async (imagePath) => {
    if (!imagePath) return;

    try {
      const query = new URLSearchParams({
        file_name: imagePath,
      });
      const response = await ApiManager.getFile(query);

      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);

      window.open(fileUrl, "_blank"); // Open file in a new tab
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const handleFileRemove = async (fieldName) => {
    const fileName = formData[fieldName];

    if (!fileName) return;

    try {
      const response = await ApiManager.storageDeleteFile(fileName);

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      setFormData((prev) => ({
        ...prev,
        [fieldName]: "",
      }));

      console.log("File deleted successfully:", fileName);
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file");
    }
  };

  const handleChange = async (e) => {
    const { name, value, type, checked, multiple, selectedOptions } = e.target;

    if (type === "file") {
      try {
        const file = e.target.files[0];
        if (!file) return;

        // Delete existing file if there is one
        if (formData[name]) {
          await handleFileRemove(name);
        }

        // Get a presigned URL for upload
        const presignedResponse = await ApiManager.storageGetPresignedUploadUrl({
          filename: file.name,
          directory: "uploads"
        });

        if (!presignedResponse.ok) {
          throw new Error("Failed to get presigned URL");
        }

        const presignedData = await presignedResponse.json();

        // The response contains: url, fileName, key, expires
        console.log("Presigned URL data:", presignedData);

        // Upload the file directly to the storage provider
        const uploadResponse = await fetch(presignedData.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file");
        }

        // Save metadata after upload using the fileName from the response
        const metadataResponse = await ApiManager.storageSaveMetadata(presignedData.fileName, presignedData.key);

        if (!metadataResponse.ok) {
          throw new Error("Failed to save metadata");
        }

        console.log("Upload successful. File will expire in:", presignedData.expires);
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: presignedData.fileName,
        }));
        console.log("upload result", presignedData);
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    } else if (type === "checkbox") {
      if (e.target.dataset.multiselect) {
        // Handle multiselect checkboxes
        setFormData((prev) => {
          const values = prev[name] || [];
          return {
            ...prev,
            [name]: checked
              ? [...values, value]
              : values.filter((v) => v !== value),
          };
        });
      } else {
        // Handle normal checkbox
        setFormData({
          ...formData,
          [name]: checked,
        });
      }
    } else if (multiple) {
      const selectedValues = Array.from(selectedOptions).map(
        (option) => option.value
      );
      setFormData({
        ...formData,
        [name]: selectedValues,
      });
    } else if (type === "datetime-local") {
      const unixTimestamp = Math.floor(new Date(value).getTime() / 1000);
      setFormData({
        ...formData,
        [name]: unixTimestamp,
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "number" ? Number(value) : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form Data Before Submit:", formData);

    setShowLoading(true);

    try {
      await withLoading('updateEntity', async (onProgress) => {
        onProgress(10);
        const response = await updateFunction(formData);
        onProgress(50);
        if (!response.ok) throw new Error(await response.text());
        onProgress(100);
        toggleFunction();
        onSuccess?.();
      }, 'Updating entity...', 30000);
    } catch (error) {
      console.error("Error updating entity:", error);
      alert(error.message || "Failed to edit Entity");
    } finally {
      setTimeout(() => {
        setShowLoading(false);
      }, 500);
    }
  };

  const handleMapInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption?.value || ''
    }));
  };

  // Get loading state for the form
  const isDataLoading = isLoading('updateEntity');
  const loadingMessage = getMessage('updateEntity');
  const loadingProgress = getProgress('updateEntity');

  return (
    <div className="add-form">
      {showLoading ? (
        <div className="loading-container">
          <LoadingSpinner
            size="large"
            message={loadingMessage || "Updating entity..."}
            progress={loadingProgress}
            showProgress={true}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {columns.map((field, index) => (

            <div
              className={`form-group ${
                field.type === "mapinput" ? "map-field" : ""
              }`}
              key={index}
            >
              <label>{field.label}:</label>
              {field.type === "file" ? (
                <CustomFileUpload
                  name={field.name}
                  // label={field.label}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required={field.required}
                  storedFileName={formData[field.name]}
                  isUploading={isLoading('updateEntity')}
                  uploadProgress={getProgress('updateEntity')}
                />
              ) : field.type === "mapinput" ? (
                <div className="form-group-map">
                  <MapInput
                    label={field.label}
                    onAddressSelect={(locationData) =>
                      handleMapInputChange(field.name, locationData)
                    }
                    initialValue={formData[field.name] || null}
                  />
                </div>
              ) : field.type === "picklist" ? (
                <CustomSelect
                  name={field.name}
                  options={field.options.map(option => ({
                    value: option,
                    label: option
                  }))}
                  value={formData[field.name]}
                  onChange={(option) => handleSelectChange(field.name, option)}
                  placeholder={`Select ${field.label}`}
                />
              ) : field.type === "multiselect" ? (
                <div className="checkbox-group">
                  {field.options.map((option, i) => (
                    <div key={i} className="checkbox-item">
                      <input
                        type="checkbox"
                        name={field.name}
                        value={option}
                        data-multiselect="true"
                        checked={formData[field.name]?.includes(option) || false}
                        onChange={handleChange}
                      />
                      <label>{option}</label>
                    </div>
                  ))}
                </div>
              ) : field.type === "checkbox" ? (
                <div className={`form-group checkbox-group`}>
                  <input
                    type="checkbox"
                    name={field.name}
                    checked={!!formData[field.name]}
                    onChange={handleChange}
                  />
                  <label>{field.label}</label>
                </div>
              ) : field.type === "datetime-local" ? (
                <input
                  type="datetime-local"
                  name={field.name}
                  defaultValue={formatDateTime(formData[field.name], "datetime-local")}
                  required={field.required}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type={field.type}
                  step={field?.step || ""}
                  name={field.name}
                  value={formData[field.name] || ""}
                  required={field.required}
                  onChange={handleChange}
                />
              )}
            </div>
          ))}
          <button type="submit">Submit</button>
        </form>
      )}
    </div>
  );
};

export default EditEntity;
